/**
 * The Set-up-Terraform snippet (Feature: terraform-provider, task 10.2; **Requirement 14.6**;
 * Design: §Components 9 item 5, §Components 11).
 *
 * `required_providers` for `DanaAIhq/soroush` plus a `provider "soroush"` block, with `endpoint` filled
 * from the deployment's own configured API endpoint — {@link useConfig}'s `apiEndpoint`, the same value
 * the SPA's client calls — so a reader does not have to know or guess the URL of the platform they are
 * already looking at.
 *
 * ## The key is an environment variable, and that is not a stylistic preference
 *
 * `api_key` is absent from the rendered block. The provider falls back to `SOROUSH_API_KEY`
 * (Requirement 1.1), which is the same variable the CLI reads, so one CI environment configures both
 * tools. The reason it must not be a literal is narrower than "secrets do not belong in files":
 * `terraform plan -out` records a saved plan file, and a saved plan records the `provider` block's
 * **literal** values. So an `api_key = "…"` in HCL reaches a plan artifact even when the state backend is
 * encrypted and the HCL is never committed — the one path by which the platform's only plaintext
 * credential comes to rest on disk (requirements §Security findings 1). The warning is rendered *inside*
 * the snippet rather than beside it, because the snippet is what gets pasted, and a caveat that lives
 * only on this page does not travel with it.
 *
 * ## Two placements, one snippet
 *
 * Requirement 14.6 wants this in two places: as a section on `/api-keys`, where an admin who just minted
 * a key is standing, and as a **collapsed** copy inside every entity's Terraform section, so a non-admin
 * who was handed a key can still reach it. The `/api-keys` page is admin-only in its entirety
 * (`api-key:read` is granted to `admin` alone), which is exactly why the second placement exists — an
 * editor can adopt a Check into Terraform and would otherwise have no way to see how to configure the
 * provider. Collapsed rather than absent, because it is reference material there and the primary content
 * on the keys page.
 *
 * Renders from configuration the app loaded at startup: no API call (Requirement 14.7).
 */

import { useConfig } from '../config-context';
import { CopyableSnippet } from '../terraform/CopyableSnippet';
import { renderProviderSetup } from '../terraform/hcl';

export interface TerraformSetupProps {
  /**
   * Render inside a closed `<details>` rather than as a page section. Used by the entity pages'
   * Terraform section (Requirement 14.6, second half).
   */
  readonly collapsed?: boolean;
}

export function TerraformSetup({ collapsed = false }: TerraformSetupProps): JSX.Element {
  const { apiEndpoint } = useConfig();
  const snippet = renderProviderSetup(apiEndpoint);

  const body = (
    <>
      <p className="muted">
        Put this in your Terraform configuration, then export your key as <code>SOROUSH_API_KEY</code>{' '}
        before running <code>terraform plan</code>. The provider reads the key from the environment; a
        literal in the <code>provider</code> block would be written to any saved plan file.
      </p>
      <CopyableSnippet
        label="Terraform provider configuration"
        text={snippet}
        testId="terraform-provider-setup"
      />
    </>
  );

  if (collapsed) {
    return (
      <details className="terraform-setup-collapsed" data-testid="terraform-setup-collapsed">
        <summary>Provider configuration</summary>
        {body}
      </details>
    );
  }

  return (
    <section className="card terraform-setup" data-testid="terraform-setup">
      <h2>Set up Terraform</h2>
      {body}
    </section>
  );
}
